/* Sezione FAQ: apertura singola, ricerca, chip categorie su mobile, deep link #hash.
   Senza JS l'accordion resta usabile grazie a <details>/<summary>. */
if (!customElements.get('faq-accordion')) {
  const normalize = (text) => text.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/\s+/g, ' ').trim();
  const fill = (template, values) => Object.keys(values).reduce((out, key) => out.replaceAll(`[${key}]`, values[key]), template || '');

  class FaqAccordion extends HTMLElement {
    // ponytail: su mobile la ricerca mostra al massimo 8 risultati, come da design
    static MOBILE_MAX_RESULTS = 8;

    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-faq-item]')];
      this.groups = [...this.querySelectorAll('[data-faq-group]')];
      this.chips = [...this.querySelectorAll('[data-faq-chip]')];
      this.input = this.querySelector('[data-faq-input]');
      this.status = this.querySelector('[data-faq-status]');
      this.empty = this.querySelector('[data-faq-empty]');
      this.more = this.querySelector('[data-faq-more]');
      this.single = this.hasAttribute('data-single');
      this.mobile = window.matchMedia('(max-width: 767.98px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeCat = this.chips[0]?.dataset.faqChip;

      this.items.forEach((item) => {
        item.searchText = normalize(item.textContent);
        item.querySelector('summary').addEventListener('click', (event) => this.onSummaryClick(event, item));
        item.addEventListener('toggle', () => this.onToggle(item));
      });

      if (this.single) {
        const open = this.items.filter((item) => item.open);
        open.slice(1).forEach((item) => { item.open = false; });
      }

      this.input?.addEventListener('input', () => this.update());
      this.input?.form.addEventListener('submit', (event) => event.preventDefault());
      this.querySelector('[data-faq-clear]')?.addEventListener('click', () => {
        this.input.value = '';
        this.update();
        this.input.focus();
      });
      this.chips.forEach((chip) => chip.addEventListener('click', () => this.selectCategory(chip.dataset.faqChip, true)));

      this.onHashChange = () => this.openFromHash();
      this.onMediaChange = () => this.update();
      window.addEventListener('hashchange', this.onHashChange);
      this.mobile.addEventListener('change', this.onMediaChange);

      this.update();
      this.openFromHash();
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this.onHashChange);
      this.mobile.removeEventListener('change', this.onMediaChange);
    }

    onToggle(item) {
      if (!this.single || !item.open) return;
      this.items.forEach((other) => {
        if (other !== item && other.open) other.open = false;
      });
    }

    onSummaryClick(event, item) {
      if (this.reducedMotion.matches || !item.animate) return;
      event.preventDefault();
      if (item.animation) return;

      const panel = item.querySelector('.faq-item__answer');
      const opening = !item.open;
      if (opening) item.open = true;
      const height = `${panel.scrollHeight}px`;
      item.animation = panel.animate(
        { height: opening ? ['0px', height] : [height, '0px'], opacity: opening ? [0, 1] : [1, 0] },
        { duration: 250, easing: 'ease-out' }
      );
      item.animation.onfinish = () => {
        if (!opening) item.open = false;
        item.animation = null;
      };
    }

    selectCategory(id, updateHash) {
      if (!id) return;
      this.activeCat = id;
      if (updateHash) history.replaceState(null, '', `#${id}`);
      this.update();
      this.chips.find((chip) => chip.dataset.faqChip === id)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    openFromHash() {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;
      const target = this.querySelector(`#${CSS.escape(id)}`);
      if (!target) return;

      const group = target.closest('[data-faq-group]');
      if (group && this.chips.some((chip) => chip.dataset.faqChip === group.id)) this.activeCat = group.id;
      if (target.matches('[data-faq-item]')) target.open = true;
      this.update();
      requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }

    update() {
      const query = this.input ? this.input.value.trim() : '';
      const needle = normalize(query);
      const searching = needle.length > 0;
      const chipsMode = this.chips.length > 0 && this.mobile.matches && !searching;
      const limit = searching && this.mobile.matches ? FaqAccordion.MOBILE_MAX_RESULTS : Infinity;
      let found = 0;

      this.toggleAttribute('data-searching', searching);

      this.items.forEach((item) => {
        const match = !searching || item.searchText.includes(needle);
        if (match) found += 1;
        item.hidden = !match || found > limit;
      });

      this.groups.forEach((group) => {
        const visible = group.querySelectorAll('[data-faq-item]:not([hidden])').length;
        group.hidden = chipsMode ? group.id !== this.activeCat : visible === 0;
        const count = group.querySelector('[data-faq-count]');
        if (count) {
          const total = searching ? visible : Number(count.dataset.total);
          count.textContent = fill(total === 1 ? this.dataset.countOne : this.dataset.countOther, { count: total });
        }
      });

      this.chips.forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.faqChip === this.activeCat)));

      if (this.status) {
        this.status.textContent = searching
          ? fill(found === 1 ? this.dataset.resultsOne : this.dataset.resultsOther, { count: found, query })
          : '';
      }
      if (this.empty) this.empty.hidden = !(searching && found === 0);
      if (this.more) {
        this.more.hidden = found <= limit;
        this.more.textContent = found > limit ? fill(this.dataset.more, { count: found - limit }) : '';
      }
    }
  }

  customElements.define('faq-accordion', FaqAccordion);
}
